import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatCard, Loader } from "@/components/ui-bits";
import {
  Users, UserCheck, Clock, ArrowDownToLine, DollarSign, Crown,
  Package, Layers, Sparkles, Wallet, Landmark, Loader2, Banknote,
} from "lucide-react";

function WithdrawDialog({ open, onClose, title, available, onConfirm, testid }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > available + 1e-9) return toast.error(`Amount can't exceed ${fmt(available)}`);
    setBusy(true);
    try {
      await onConfirm(amt);
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid={`${testid}-dialog`}>
      <div className="glass rounded-2xl p-6 w-full max-w-sm border border-white/10">
        <h3 className="font-display text-lg font-semibold text-white mb-1">{title}</h3>
        <p className="text-xs text-slate-400 mb-4">Available: <span className="font-num text-cyan-300">{fmt(available)}</span></p>
        <label className="text-xs uppercase tracking-wide text-slate-500">Amount</label>
        <div className="flex items-center gap-2 mt-1 mb-4 rounded-xl bg-white/5 border border-white/10 px-3">
          <span className="text-slate-400">$</span>
          <input data-testid={`${testid}-amount`} type="number" min="0" step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)} autoFocus
            className="w-full bg-transparent text-white outline-none py-2.5 font-num" placeholder="0.00" />
          <button className="text-[0.65rem] uppercase text-cyan-300 hover:text-cyan-200"
            onClick={() => setAmount(String(available))} data-testid={`${testid}-max`}>Max</button>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} disabled={busy}
            className="flex-1 py-2.5 rounded-xl border border-white/10 text-slate-300 hover:bg-white/5">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid={`${testid}-confirm`}
            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-slate-900 font-semibold">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Withdraw
          </button>
        </div>
      </div>
    </div>
  );
}

const WithdrawBtn = ({ onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/25">
    <Banknote className="h-3.5 w-3.5" /> Withdraw
  </button>
);

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [dialog, setDialog] = useState(null); // 'creator' | 'system' | null

  const load = () => API.get("/admin/stats").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  if (!s) return <Loader label="Loading overview" />;

  const withdraw = (kind) => async (amount) => {
    const url = kind === "creator" ? "/admin/withdraw/creator" : "/admin/withdraw/system-fund";
    const r = await API.post(url, { amount });
    toast.success(r.data.message);
    await load();
  };

  return (
    <div>
      <PageHeader title="Admin Overview" subtitle="Platform-wide metrics, sales, fees, creator income and pending approvals." testid="admin-header" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="admin-total-users" label="Total Users" value={s.total_users} icon={Users} accent="emerald" />
        <StatCard testid="admin-active-users" label="Active Users" value={s.active_users} icon={UserCheck} accent="cyan" />
        <StatCard testid="admin-pending-deposits" label="Pending Deposits" value={s.pending_deposits} icon={Clock} accent="gold" />
        <StatCard testid="admin-pending-withdrawals" label="Pending Withdrawals" value={s.pending_withdrawals} icon={ArrowDownToLine} accent="rose" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="admin-fund" label="Admin Fund" value={s.admin_fund} money icon={Landmark} accent="emerald" sub="Source for user funding" />
        <StatCard testid="admin-system-fund" label="System Fund" value={s.system_fund} money icon={Landmark} accent="cyan"
          sub="Package Sales − Creator income"
          action={<WithdrawBtn testid="withdraw-system-btn" onClick={() => setDialog("system")} />} />
        <StatCard testid="admin-creator" label="Creator Income" value={s.creator_wallet} money icon={Crown} accent="violet"
          sub="Available to withdraw"
          action={<WithdrawBtn testid="withdraw-creator-btn" onClick={() => setDialog("creator")} />} />
        <StatCard testid="admin-deposits" label="Total Deposits" value={s.total_deposits} money icon={Wallet} accent="cyan" />
        <StatCard testid="admin-withdrawals" label="Total Withdrawals" value={s.total_withdrawals} money icon={ArrowDownToLine} accent="cyan" />
        <StatCard testid="admin-fees" label="Platform Fees" value={s.total_platform_fees} money icon={DollarSign} accent="gold" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="admin-package-sales" label="Package Sales" value={s.total_package_sales} money icon={Package} accent="emerald" />
        <StatCard testid="admin-level-income" label="Total Level Income" value={s.total_level_income} money icon={Layers} accent="cyan" />
        <StatCard testid="admin-magic" label="Magic Card Income" value={s.magic_card_income} money icon={Sparkles} accent="violet" />
        <StatCard testid="admin-total-fund" label="Deposit Fund Wallet" value={s.total_deposit_fund} money icon={Wallet} accent="gold" sub="Sum of all users' Deposit wallet" />
        <StatCard testid="admin-total-main-fund" label="Total Main Fund" value={s.total_main_fund} money icon={Wallet} accent="emerald" sub="Sum of all users' Main wallet" />
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-lg font-semibold text-white mb-4">Package-wise Users</h3>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(s.package_users).map(([name, count]) => (
            <div key={name} className="rounded-xl bg-white/5 border border-white/10 p-5 text-center">
              <div className="text-xs uppercase tracking-wide text-cyan-300">{name}</div>
              <div className="font-num text-3xl font-bold text-white mt-1">{count}</div>
              <div className="text-xs text-slate-500">users</div>
            </div>
          ))}
        </div>
      </div>

      <WithdrawDialog open={dialog === "creator"} onClose={() => setDialog(null)}
        title="Withdraw Creator Income" available={s.creator_wallet}
        onConfirm={withdraw("creator")} testid="withdraw-creator" />
      <WithdrawDialog open={dialog === "system"} onClose={() => setDialog(null)}
        title="Withdraw System Fund" available={s.system_fund}
        onConfirm={withdraw("system")} testid="withdraw-system" />
    </div>
  );
}
